import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import { Plus, Search, Edit2, Trash2, Eye, Phone, Mail, Building2 } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY_FORM = {
  client_name: '', client_type: 'Individual', contact_first_name: '',
  contact_last_name: '', email: '', phone: '', address: '',
  billing_method: 'Hourly', tax_id: '', id_number: '', gender: '', date_of_birth: '',
};

// Defined OUTSIDE Clients so React sees a stable component reference on every render.
function ClientField({ label, name, type = 'text', options, form, onChange }) {
  return (
    <div>
      <label className="label">{label}</label>
      {options ? (
        <select className="input" value={form[name] || ''} onChange={e => onChange(name, e.target.value)}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} className="input" value={form[name] || ''} onChange={e => onChange(name, e.target.value)} />
      )}
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await clientsAPI.list({ search: q });
      setClients(data.results || data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c, date_of_birth: c.date_of_birth || '' }); setModalOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await clientsAPI.update(editing.id, form);
        toast.success('Client updated');
      } else {
        await clientsAPI.create(form);
        toast.success('Client created');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error('Failed to save client');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await clientsAPI.delete(id);
      toast.success('Client deleted');
      load();
    } catch { toast.error('Cannot delete client with associated records'); }
  };

  const handleChange = (name, value) => setForm(f => ({ ...f, [name]: value }));

  return (
    <div className="p-6">
      <PageHeader
        title="Clients"
        subtitle={`${clients.length} clients`}
        actions={<button className="btn-primary" onClick={openAdd}><Plus size={16} />Add Client</button>}
      />

      {/* Search */}
      <div className="card mb-5 p-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input pl-9"
            placeholder="Search clients by name, email, phone..."
            value={search}
            onChange={e => { setSearch(e.target.value); load(e.target.value); }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Client', 'Type', 'Contact', 'Billing', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400">No clients found. Add your first client.</td></tr>
              ) : clients.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-700 font-semibold text-sm">{c.client_name[0]}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{c.client_name}</p>
                        {c.id_number && <p className="text-xs text-gray-400">ID: {c.id_number}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Building2 size={13} />
                      {c.client_type}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-0.5">
                      {c.email && <div className="flex items-center gap-1 text-gray-600"><Mail size={12} />{c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1 text-gray-600"><Phone size={12} />{c.phone}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.billing_method}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.is_active ? 'Active' : 'Inactive'} /></td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" onClick={() => navigate(`/clients/${c.id}`)}><Eye size={15} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" onClick={() => openEdit(c)}><Edit2 size={15} /></button>
                      <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" onClick={() => handleDelete(c.id)}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Client' : 'New Client'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><ClientField label="Client/Company Name *" name="client_name" form={form} onChange={handleChange} /></div>
            <ClientField label="Type" name="client_type" options={['Individual', 'Company']} form={form} onChange={handleChange} />
            <ClientField label="Billing Method" name="billing_method" options={['Hourly', 'Flat Fee', 'Contingency']} form={form} onChange={handleChange} />
            <ClientField label="Contact First Name" name="contact_first_name" form={form} onChange={handleChange} />
            <ClientField label="Contact Last Name" name="contact_last_name" form={form} onChange={handleChange} />
            <ClientField label="Email" name="email" type="email" form={form} onChange={handleChange} />
            <ClientField label="Phone" name="phone" type="tel" form={form} onChange={handleChange} />
            <ClientField label="ID Number" name="id_number" form={form} onChange={handleChange} />
            <ClientField label="Gender" name="gender" options={['', 'Male', 'Female', 'Other']} form={form} onChange={handleChange} />
            <ClientField label="Date of Birth" name="date_of_birth" type="date" form={form} onChange={handleChange} />
            <ClientField label="Tax ID / VAT Number" name="tax_id" form={form} onChange={handleChange} />
            <div className="col-span-2">
              <label className="label">Address</label>
              <textarea className="input" rows={2} value={form.address || ''} onChange={e => handleChange('address', e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Client' : 'Add Client'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
